import React from 'react';
import type { RagResultChunk } from '../types';

// ============================================================================
// Constants
// ============================================================================
const INFO_COLOR = '#3b82f6';
const PURPLE_COLOR = '#8b5cf6';
const SUCCESS_COLOR = '#22c55e';
const DARK_COLOR = '#444444';

// ============================================================================
// Props
// ============================================================================
export interface RagChunkCardProps {
  chunk: RagResultChunk;
  index?: number;
  showSimilarity?: boolean;
  compact?: boolean;
}

// ============================================================================
// Component
// ============================================================================
export const RagChunkCard: React.FC<RagChunkCardProps> = ({
  chunk,
  index,
  showSimilarity = false,
  compact = false,
}) => {
  const contentType = chunk.content_type || 'text';
  const similarity = chunk.similarity ?? chunk.score;
  const content = chunk.content || chunk.raw_content || '(No content available)';
  const filename = chunk.filename || chunk.source || 'Unknown source';
  const chunkId = chunk.id || chunk.chunk_id;

  return (
    <div
      style={{
        padding: compact ? '0.75rem' : '1rem',
        marginBottom: '0.75rem',
        borderRadius: '4px',
        border: '1px solid #e2e8f0',
        background: '#f8fafc',
        transition: 'all 0.15s',
      }}
    >
      {/* Header row */}
      <div style={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'flex-start',
        marginBottom: '0.5rem',
        flexWrap: 'wrap',
        gap: '0.5rem',
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', flexWrap: 'wrap' }}>
          {/* Content type badge */}
          <span
            style={{
              display: 'inline-block',
              padding: '0.125rem 0.5rem',
              background: contentType === 'table' ? PURPLE_COLOR : INFO_COLOR,
              color: 'white',
              borderRadius: '4px',
              fontSize: '0.65rem',
              textTransform: 'uppercase',
            }}
          >
            {contentType}
          </span>

          {/* Filename */}
          <strong style={{ fontSize: '0.8rem', color: DARK_COLOR }}>
            {filename}
          </strong>

          {/* Index/chunk number */}
          {index !== undefined && (
            <span style={{ fontSize: '0.7rem', color: '#64748b' }}>
              #{index + 1}
            </span>
          )}
        </div>

        {/* Similarity score */}
        {showSimilarity && similarity !== undefined && (
          <span
            style={{
              fontSize: '0.7rem',
              fontWeight: 500,
              padding: '0.25rem 0.5rem',
              borderRadius: '4px',
              background: similarity > 0.8 ? '#dcfce7' : similarity > 0.5 ? '#fef3c7' : '#fee2e2',
              color: similarity > 0.8 ? SUCCESS_COLOR : similarity > 0.5 ? '#ca8a04' : '#dc2626',
            }}
          >
            {Math.round(similarity * 100)}% match
          </span>
        )}
      </div>

      {/* Chunk ID (if available and not compact) */}
      {!compact && chunkId && (
        <div style={{
          fontSize: '0.65rem',
          color: '#94a3b8',
          fontFamily: 'monospace',
          marginBottom: '0.5rem',
        }}>
          ID: {chunkId}
        </div>
      )}

      {/* Content */}
      <div style={{
        fontSize: '0.8rem',
        lineHeight: 1.5,
        color: '#334155',
        background: 'white',
        padding: '0.5rem 0.75rem',
        borderRadius: '4px',
        border: '1px solid #e2e8f0',
        maxHeight: compact ? '60px' : '120px',
        overflow: 'hidden',
        whiteSpace: 'pre-wrap',
        wordBreak: 'break-word',
      }}>
        {content}
      </div>
    </div>
  );
};

// ============================================================================
// List Component
// ============================================================================
export interface RagChunkListProps {
  chunks: RagResultChunk[];
  title?: string;
  showSimilarity?: boolean;
  compact?: boolean;
  maxVisible?: number;
  emptyMessage?: string;
}

export const RagChunkList: React.FC<RagChunkListProps> = ({
  chunks,
  title = 'RAG Chunks',
  showSimilarity = true,
  compact = false,
  maxVisible,
  emptyMessage = 'No chunks available',
}) => {
  const [expanded, setExpanded] = React.useState(false);

  if (!chunks || chunks.length === 0) {
    return (
      <div style={{
        padding: '1rem',
        borderRadius: '4px',
        background: '#f9fafb',
        color: '#6b7280',
        fontSize: '0.875rem',
        fontStyle: 'italic',
      }}>
        {emptyMessage}
      </div>
    );
  }

  const displayedChunks = maxVisible && !expanded
    ? chunks.slice(0, maxVisible)
    : chunks;

  const hasMore = maxVisible && chunks.length > maxVisible;

  return (
    <div>
      {title && (
        <div style={{
          fontSize: '0.75rem',
          fontWeight: 600,
          color: '#374151',
          marginBottom: '0.75rem',
          display: 'flex',
          alignItems: 'center',
          gap: '0.5rem',
        }}>
          <span>🗃️ {title}</span>
          <span style={{
            fontSize: '0.65rem',
            background: '#e5e7eb',
            padding: '0.125rem 0.5rem',
            borderRadius: '9999px',
            color: '#6b7280',
          }}>
            {chunks.length} chunk{chunks.length !== 1 ? 's' : ''}
          </span>
        </div>
      )}

      {displayedChunks.map((chunk, idx) => (
        <RagChunkCard
          key={chunk.id || chunk.chunk_id || idx}
          chunk={chunk}
          index={idx}
          showSimilarity={showSimilarity}
          compact={compact}
        />
      ))}

      {hasMore && (
        <button
          onClick={() => setExpanded(!expanded)}
          style={{
            width: '100%',
            padding: '0.5rem',
            border: '1px dashed #d1d5db',
            borderRadius: '4px',
            background: 'transparent',
            color: INFO_COLOR,
            fontSize: '0.8rem',
            cursor: 'pointer',
            marginTop: '0.5rem',
          }}
        >
          {expanded
            ? `Show less`
            : `Show ${chunks.length - maxVisible} more chunk${chunks.length - maxVisible !== 1 ? 's' : ''}`}
        </button>
      )}
    </div>
  );
};

export default RagChunkCard;
